import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { GetBookingsQuery } from "../../../__generated__/graphql";
import { useNotifications } from "../../../components/Notification";
import styles from "./Cancel.module.scss";

type CancelBookingArgs = {
  booking: GetBookingsQuery["bookings"]["records"][number];
};

export default function CancelBooking({ booking }: CancelBookingArgs) {
  const { showNotification } = useNotifications();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelledByCustomer, setCancelledByCustomer] = useState(false);
  const [cancellationReason, setCancellationReason] = useState<string | null>(
    null,
  );
  const [cancelBooking, { loading }] = useMutation(CANCEL_BOOKING);

  if (booking.state !== "CONFIRMED") return null;

  const reservationDateTime = DateTime.fromFormat(
    `${booking.group.date} ${booking.group.time}`,
    "yyyy-MM-dd HH:mm",
  );
  const canBeRefundedUntil = DateTime.fromFormat(
    booking.canBeRefundedUntil,
    "yyyy-MM-dd HH:mm",
    { zone: "Asia/Tokyo" },
  );
  const canBeRefunded =
    canBeRefundedUntil > DateTime.now().setZone("Asia/Tokyo");

  return (
    <>
      <Tooltip
        title={
          !canBeRefunded ? (
            <>
              Sorry, this booking can not be cancelled anymore
              <br />
              <br />
              <span style={{ fontSize: "10px" }}>
                (could be cancelled until: {booking.canBeRefundedUntil})
              </span>
            </>
          ) : null
        }
      >
        <div style={{ display: "inline-block" }}>
          <Button
            size="small"
            variant="contained"
            color="inherit"
            className={styles.cancelButton}
            onClick={() => setDialogOpen(true)}
            disabled={!canBeRefunded}
          >
            Cancel
          </Button>
        </div>
      </Tooltip>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogContent>
          <>
            Are you sure you want to cancel booking
            <br />
            <b>{booking.id}</b>?<br />
            <br />
            {(booking.guests ?? [])
              .map(({ givenName, familyName }) =>
                `${givenName} ${familyName}`.trim(),
              )
              .join(", ")}
            <br />
            {booking.group.product.translation?.name ?? "—"}
            <br />
            {reservationDateTime.toFormat("cccc LLLL d yyyy, HH:mm")}
            <br />
            <br />
            <span style={{ fontSize: "12px" }}>
              (can be cancelled until: {booking.canBeRefundedUntil})
            </span>
            <br />
            <br />
            <hr />
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    value={cancelledByCustomer}
                    onChange={(e) => setCancelledByCustomer(e.target.checked)}
                  />
                }
                label="Cancellation requested by customer?"
              />
              <TextField
                sx={{ width: "100%" }}
                size="small"
                multiline
                label="Cancellation reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
              />
            </Stack>
          </>
        </DialogContent>
        <DialogActions>
          {canBeRefunded && (
            <Button
              color="error"
              disabled={!canBeRefunded || loading || !canCancel()}
              onClick={handleCancelBooking}
            >
              {!loading ? <>Yes</> : <CircularProgress size={20} />}
            </Button>
          )}
          <Button
            color="inherit"
            disabled={loading}
            onClick={() => setDialogOpen(false)}
          >
            {canBeRefunded ? <>No</> : <>Close</>}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  function canCancel() {
    if (!cancellationReason) return false;
    return true;
  }

  async function handleCancelBooking() {
    const { errors } = await cancelBooking({
      variables: {
        input: {
          id: booking.id,
          cancelledByCustomer,
          cancellationReason,
        },
      },
      refetchQueries: ["GetBookings"],
    });

    if (errors) {
      showNotification({ severity: "error", message: errors[0].message });
    } else {
      showNotification({
        severity: "success",
        message: `Booking has been cancelled`,
      });
      setDialogOpen(false);
    }
  }
}

const CANCEL_BOOKING = gql`
  mutation CancelBooking($input: CancelBookingInput!) {
    cancelBooking(input: $input)
  }
`;
